<template>
	<!-- Mobile View: Shown for < lg, hidden at lg+ -->
	<div class="block lg:hidden">
		<!-- Mobile Overlay (shown when showMobileCart is true) -->
		<div :class="showMobileCart ? 'fixed flex lg:hidden w-full z-50 h-full top-0 inset-0 bg-ihh-black/50 backdrop-blur-sm sm:px-6' : 'hidden'">
			<div class="flex-col flex w-full justify-center items-center h-full">
				<div class="flex-col border bg-black border-ihh-gray/10 rounded bg-ihh-white shadow-lg w-full max-w-2xl h-full sm:max-h-[550px] overflow-scroll">
					<!-- Header -->
					<div
						class="sticky top-0 border-b relative bg-ihh-white text-ihh-midnight-gray text-xl sm:text-4xl font-semibold text-center flex w-full justify-center z-10 px-4 sm:px-2"
					>
						<div class="flex m-auto items-center justify-between w-full py-4 lg:py-4 my-2 border-ihh-slate-gray/20">
							<div class="order-2 lg:order-none">
								<div class="text-lg sm:text-xl lg:text-base text-ihh-midnight-gray font-bold">Order Summary</div>
								<div class="flex divide-x divide-ihh-midnight-gray font-normal text-base lg:text-sm text-ihh-gray-600 mt-1">
									<div class="flex px-4 block">
										{{ (store?.cart?.cartItems?.length || 0) + (store?.cart?.certificates?.length || 0) }}
										{{ store.cartTotalItems === 1 ? 'Item' : 'Items' }}
									</div>
									<div v-if="store?.cartTotalItems" class="px-4 block font-bold">
										<Spinner v-if="store.cart.tax === null" :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
										<div v-else>
											{{ checkout.displayTotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
										</div>
									</div>
								</div>
							</div>

							<router-link to="/cart" class="text-sm lg:text-xs text-ihh-dark-salmon hover:text-ihh-salmon cursor-pointer order-1 lg:order-none">
								Edit Cart
							</router-link>

							<div class="lg:hidden order-4 lg:order-none">
								<button
									@click="toggleMobileCart"
									class="flex group w-7 h-7 p-2 justify-around z-10 hover:bg-ihh-slate-gray bg-ihh-gray/20 rounded-full font-bold text-xl text-ihh-midnight-gray"
								>
									<div class="m-auto items-center flex w-full justify-center">
										<div class="rotate-45 absolute rounded bg-ihh-midnight-gray group-hover:bg-ihh-white py-[0.5px] rounded-lg px-1"></div>
										<div class="-rotate-45 rounded bg-ihh-midnight-gray group-hover:bg-ihh-white py-[0.5px] rounded-lg px-1"></div>
									</div>
								</button>
							</div>
						</div>
					</div>
					<!-- Cart Items -->
					<CheckoutCart />

					<!-- Mobile Trust Icons -->
					<div class="flex w-full justify-center space-x-6 mb-14">
						<img class="w-16 h-16" src="/images/trust-icon-shipping-0129.jpg" />
						<img class="w-16 h-16" src="/images/trust-icon-security-0129.jpg" />
						<img class="w-16 h-16" src="/images/trust-icon-satisfaction-0129.jpg" />
					</div>

					<!-- Return to Checkout Button -->
					<div class="sticky bottom-8 w-full justify-center items-center sm:hidden z-20">
						<div class="flex w-full justify-center">
							<div class="">
								<button
									@click="toggleMobileCart"
									class="font-semibold uppercase text-sm px-20 py-2 rounded text-center text-white bg-ihh-dark-salmon mobile-return-button"
								>
									Return to Checkout
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Mobile Bottom Banner -->
		<div id="mobile-order-summary-banner" class="fixed left-0 right-0 bottom-6 z-10 px-4" v-if="!orderSummary">
			<div
				class="flex items-center w-full max-w-xl mx-auto bg-ihh-white shadow-lg p-3 rounded-lg border border-ihh-gray/50 cursor-pointer"
				@click="showMobileCart = true"
			>
				<div v-if="store?.cartTotalItems" class="mr-4 flex-shrink-0">
					<div class="bg-ihh-white rounded-lg overflow-hidden flex items-center justify-center w-14 h-14">
						<img
							v-if="store?.cart?.cartItems?.length > 0"
							:src="store.cart?.cartItems[0]?.item?.images?.find((image) => image.isThumbnail)?.url"
							class="w-12 h-12 object-contain"
						/>
					</div>
				</div>

				<div class="flex w-full items-center" :class="[!store.cartTotalItems ? 'justify-end' : 'justify-between']" style="font-family: 'Montserrat', sans-serif">
					<div class="flex flex-col" :class="[!store.cartTotalItems ? 'items-center' : '']">
						<div class="text-base font-semibold text-ihh-midnight-gray">
							{{ (store?.cart?.cartItems?.length || 0) + (store?.cart?.certificates?.length || 0) }} {{ store.cartTotalItems === 1 ? 'Item' : 'Items' }}
						</div>
						<div class="flex justify-start">
							<button v-if="store.cartTotalItems" @click="showMobileCart = true" class="text-xs text-ihh-dark-salmon text-start underline underline-offset-2">
								Show Details
							</button>
							<a v-else href="/" class="text-xs text-ihh-dark-salmon text-start underline underline-offset-2"> Continue to Home </a>
						</div>
					</div>
					<div v-if="store?.cartTotalItems" class="flex items-center text-ihh-midnight-gray">
						<div class="text-xl font-bold tracking-wide">
							<Spinner v-if="store.cart.tax === null" :small="true" class="w-4 h-4" color="border-t-ihh-salmon" />
							<div class="text-xl font-bold" v-else>
								{{ checkout.displayTotal?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Desktop View: Hidden for < lg, shown at lg+ -->
	<div class="hidden lg:block">
		<div class="flex justify-center w-full lg:w-[378px] lg:relative">
			<div class="flex-col md:items-center w-[378px] sm:mx-0">
				<div class="flex-col flex w-full justify-center items-center h-full lg:max-w-[378px]">
					<div class="flex-col border bg-ihh-white border-ihh-gray/50 rounded overflow-hidden shadow-lg w-full max-w-2xl lg:max-w-[378px] h-fit lg:max-w-md">
						<!-- Header -->
						<div class="top-0 border-b relative bg-ihh-white text-ihh-midnight-gray text-xl font-semibold text-center flex w-full justify-center px-4 sm:px-2">
							<div class="flex m-auto items-center justify-between w-full py-4 lg:py-4 my-2 border-ihh-slate-gray/20">
								<div class="order-2 lg:order-none">
									<div class="text-lg sm:text-xl lg:text-base text-ihh-midnight-gray font-bold">Order Summary</div>
								</div>

								<router-link to="/cart" class="text-sm lg:text-xs text-ihh-dark-salmon hover:text-ihh-salmon cursor-pointer order-1 lg:order-none">
									Edit Cart
								</router-link>
							</div>
						</div>

						<!-- Item Count -->
						<div
							class="pt-4 pl-4 flex w-full justify-start text-start text-lg text-ihh-form-input-entry"
							:class="
								store.cartTotalItems === 0
									? 'flex w-full justify-center text-center text-xl font-bold pt-6'
									: 'pt-4 pl-4 flex w-full justify-start text-start text-sm'
							"
						>
							{{ (store?.cart?.cartItems?.length || 0) + (store?.cart?.certificates?.length || 0) }} {{ store.cartTotalItems === 1 ? 'Item' : 'Items' }}
						</div>

						<!-- Cart Items -->
						<CheckoutCart :isDigitalOnly="isDigitalOnly" />
					</div>
				</div>

				<!-- Desktop Trust Icons -->
				<div class="flex w-full justify-center space-x-8 my-4 pt-6">
					<img class="w-20 h-20" src="/images/trust-icon-shipping-0129.jpg" />
					<img class="w-20 h-20" src="/images/trust-icon-security-0129.jpg" />
					<img class="w-20 h-20" src="/images/trust-icon-satisfaction-0129.jpg" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Coupon from './coupon.vue'
import { useCheckoutStore } from '../../stores/store.js'
import CheckoutCart from './checkout-cart.vue'
export default {
	name: 'OrderSummary',
	inject: ['store'],

	props: {
		isDigitalOnly: {
			type: Boolean,
			default: false,
		},
	},
	components: { CheckoutCart },

	data() {
		return {
			checkout: useCheckoutStore(),
			showMobileCart: false,
		}
	},
	methods: {
		toggleMobileCart() {
			this.showMobileCart = !this.showMobileCart
		},
	},
}
</script>

<style scoped>
.mobile-return-button {
	position: relative;
	box-shadow:
		0 10px 15px -3px rgba(0, 0, 0, 0.1),
		0 4px 6px -2px rgba(0, 0, 0, 0.05);
	transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
	outline: none;
}

.mobile-return-button:hover {
	transform: translateY(-5px);
	box-shadow:
		0 20px 25px -5px rgba(0, 0, 0, 0.1),
		0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.mobile-return-button:active {
	transform: translateY(1px);
	box-shadow: 0 5px 15px -3px rgba(0, 0, 0, 0.1);
}
</style>
